/**
 * Ce qu'un niveau s'appelle, et ce qu'il apporte, en français.
 *
 * L'analyseur rend des faits : la cellule qu'un niveau distribue, le
 * renversement qui change, les deux notes tenues, les dix-sept ornements qui
 * arrivent. Il ne rend pas de phrases, et c'est délibéré des deux côtés. Un
 * service d'analyse n'a pas d'opinion sur le français, et une observation qui
 * porterait sa phrase ne pourrait plus être comptée, filtrée ni comparée, alors
 * que le parcours fait les trois.
 *
 * Ce fichier est donc la moitié pédagogique du module, et la seule qui reste
 * ici. Il n'a le droit de rien calculer.
 */

import type { FigureDegree, Level, Observation } from "./client";

/** Un niveau, tel que le joueur le lit. */
export type RungWords = {
  readonly label: string;
  readonly summary: string;
  /**
   * Ce que le niveau laisse derrière lui, et qu'aucun niveau suivant ne
   * reprend même quand il redispose les mains.
   *
   * C'est l'invariant du programme, écrit en toutes lettres : les connaissances
   * sont monotones, les doigts ne le sont pas.
   */
  readonly concepts: readonly string[];
};

const RUNGS: Record<string, RungWords> = {
  accords: {
    label: "Accords",
    summary: "fondamentale à gauche, triade à droite, en position fondamentale",
    concepts: ["fondamentale", "triade", "majeur, mineur, diminué, augmenté", "rythme harmonique", "degré"],
  },
  renversements: {
    label: "Renversements",
    summary: "la triade se pose au plus près, la main cesse de sauter",
    concepts: ["état fondamental", "premier renversement", "deuxième renversement", "position d'un accord"],
  },
  pulsation: {
    label: "Pulsation",
    summary: "le même accord, refrappé au rythme de l'accompagnement, chaque main au sien",
    concepts: ["pulsation", "rythme d'accompagnement", "basse et accord alternés", "temps fort", "contretemps"],
  },
  basse: {
    label: "Basse publiée",
    summary: "la main gauche prend la partition, la droite garde son accord",
    concepts: ["renversement publié", "accord sur basse indiquée", "ligne de basse", "pédale", "basse chromatique"],
  },
  voicings: {
    label: "Voicings réels",
    summary: "les notes de soutien telles que l'arrangeur les dispose",
    concepts: ["voicing", "disposition serrée ou large", "doublure", "septième écrite", "note tenue"],
  },
  texture: {
    label: "Texture",
    summary: "les répétitions et les doublures qui donnent au morceau sa main",
    concepts: ["figuration", "accord brisé écrit", "ostinato", "cellule rythmique", "strate"],
  },
  melodie: {
    label: "Mélodie",
    summary: "la ligne principale, ses notes d'accord et ses répétitions",
    concepts: ["ligne principale", "voix supérieure", "phrase", "contour", "harmonisation"],
  },
  etrangeres: {
    label: "Notes étrangères",
    summary: "passage, broderie, retard, appoggiature, anticipation, échappée",
    concepts: ["note de passage", "broderie", "anticipation", "retard", "appoggiature", "échappée"],
  },
  details: {
    label: "Détails",
    summary: "tout ce qui reste, y compris ce qu'aucune analyse ne sait nommer",
    concepts: ["fonction ambiguë", "non classée provisoirement"],
  },
  morceau: {
    label: "Le morceau",
    summary: "la partition entière, d'un seul tenant",
    concepts: ["par cœur"],
  },
};

/** Les mots d'un niveau, ou un repli qui ne ment pas si l'échelle change. */
export function rungWords(rungId: string): RungWords {
  return RUNGS[rungId] ?? { label: rungId, summary: "", concepts: [] };
}

/**
 * Le contrat de la moitié où l'on se trouve.
 *
 * C'est la seule chose qui sépare un modèle harmonique assumé d'un mensonge sur
 * la partition, et une version antérieure du programme s'est cassée exactement
 * là : elle fabriquait des notes en prétendant enseigner le morceau.
 *
 * ## Ce que la moitié fabriquée promet, et ce qu'elle ne promet plus
 *
 * Elle a longtemps promis un registre fixe, le même sur tous les morceaux. Ce
 * n'est plus vrai : la moitié générée pose sa main gauche là où le morceau pose
 * la sienne, section par section et à l'octave près, et la droite se pose une
 * octave au-dessus de la gauche. Ce qui se transporte d'un morceau à l'autre est
 * le **geste**, doigté, forme de la main et écart des bras, que la transposition
 * à l'octave laisse rigoureusement intact ; ce qui suit le morceau est la
 * hauteur du bras.
 *
 * L'écart des deux mains est donc constant, ce qu'il n'était pas : les deux
 * visaient chacune leur voix, et comme elles jouent la même classe de hauteur
 * leur distance ne pouvait tomber que sur un multiple de douze. Elle sautait de
 * 12 à 36 d'un accord au suivant sur 42 % des fenêtres du catalogue, pour une
 * médiane de 24 là où les morceaux tiennent leurs mains à 8.
 *
 * Et l'échafaudage ne monte plus **à la couture**. La main droite se posait une
 * octave plus haut au barreau même où la gauche passait à la partition, donc
 * deux mains changeaient d'un coup. La levée qui la sort des touches de
 * l'accompagnement écrit est désormais décidée avant le premier niveau, une
 * fois pour le morceau : mesurée sur 40 morceaux et 3 684 fenêtres, la main
 * droite change à la couture sur **zéro** d'entre elles, contre 93 % avant.
 */
export function contractOf(level: Level): string {
  if (level.realisation === "modele") {
    return (
      "Ces notes ne sont pas celles du morceau. C'est un modèle harmonique volontaire : le même geste d'un " +
      "morceau à l'autre, posé à la hauteur où celui-ci se joue."
    );
  }

  if (level.scaffolded) {
    return (
      "La main gauche joue la partition, à sa hauteur et avec sa durée. La main droite garde encore l'accord du " +
      "modèle, exactement là où elle le tenait : elle ne bouge pas d'une touche, et elle le lâchera quand la " +
      "partition aura quelque chose à lui donner."
    );
  }

  return "Les notes de la partition, à leur hauteur, à leur moment, avec leur durée. Plus rien n'est fabriqué.";
}

// ── Les observations ───────────────────────────────────────────

const ORNAMENTS: Record<string, [string, string]> = {
  passing: ["une note de passage", "notes de passage"],
  neighbour: ["une broderie", "broderies"],
  appoggiatura: ["une appoggiature", "appoggiatures"],
  suspension: ["un retard", "retards"],
  retardation: ["un retard", "retards"],
  anticipation: ["une anticipation", "anticipations"],
  escape: ["une échappée", "échappées"],
};

/** Un degré, écrit comme un musicien l'écrit : `1`, `b3`, `#5`, `10`. */
export function degreeLabel(degree: FigureDegree): string {
  const alteration = degree.alteration ?? 0;

  return (alteration < 0 ? "♭".repeat(-alteration) : "♯".repeat(alteration)) + degree.number;
}

function list(items: string[]): string {
  if (items.length <= 1) {
    return items[0] ?? "";
  }

  return `${items.slice(0, -1).join(", ")} et ${items[items.length - 1]}`;
}

/**
 * Ce qu'une observation dit au joueur, sur ce passage précis.
 *
 * Une phrase par fait, jamais une phrase par niveau : sur un passage donné un
 * niveau peut ne rien changer du tout, et une phrase écrite d'avance mentirait
 * quelque part dans chaque morceau.
 */
export function describe(o: Observation): string {
  switch (o.kind) {
    case "inversion": {
      // `degrees[0]` est la note la plus grave de la **main droite** : la
      // gauche garde la fondamentale, donc c'est la main qui change de
      // position, pas l'accord entier. Et rien ne garantit qu'elle reste où
      // elle était : l'analyseur la pose près de sa place habituelle, pas près
      // de l'accord d'avant.
      const lowest = o.degrees?.[0];

      if (lowest === undefined) {
        return "la main droite prend l'accord dans une autre position";
      }

      return simpleDegree(lowest.number) === 1
        ? "la main droite garde l'accord à l'état fondamental, dans un autre registre"
        : `la main droite prend l'accord à partir de sa ${ordinal(lowest)}`;
    }

    case "pulse":
      // `after` est le compte d'instants frappés dans la fenêtre, les deux
      // mains ensemble, et `before` celui du niveau précédent, qui vaut un :
      // les deux mains ouvrent la fenêtre au même instant. La phrase dit donc
      // ce qui s'ajoute.
      return (o.after ?? 0) - (o.before ?? 0) > 1
        ? `l'accord se refrappe au rythme de l'accompagnement, ${(o.after ?? 0) - (o.before ?? 0)} frappes de plus`
        : "l'accord se refrappe où l'accompagnement le refrappe";

    case "publishedBass": {
      // Le fait principal du niveau, et il ne dépend pas du mouvement : sur
      // 51,4 % des fenêtres, la note par laquelle la main gauche entre n'est
      // pas la fondamentale. C'est l'accord sur basse indiquée, le « /B » de
      // « Em/B », et c'est ce qu'aucune lecture de la grille ne donne.
      //
      // Le renversement ne s'annonce que si l'analyseur dit que cette première
      // note est une note de l'accord : une pédale ou une note de passage a
      // aussi un degré, et « l'accord se pose sur sa quarte » serait faux.
      // Absent, le champ ne vaut pas oui.
      const entry = o.degrees?.[0];

      if (o.entryChordTone === false) {
        return "la main gauche entre sur une note hors de l'accord" + bassMotion(o, false);
      }

      // « L'accord » en incise et non en sujet : il est masculin, donc le
      // « elle » des suites ne peut renvoyer qu'à la main gauche.
      const head =
        entry !== undefined && o.entryChordTone === true && simpleDegree(entry.number) !== 1
          ? `la main gauche entre sur la ${ordinal(entry)}, l'accord se pose dessus`
          : "la main gauche prend la basse que le morceau écrit";

      return head + bassMotion(o, true);
    }

    // `added` compte **toutes** les notes que la fenêtre gagne, et pas
    // seulement celles de la nature nommée : la phrase ne donne donc pas de
    // chiffre qu'elle attribuerait à une seule catégorie.
    case "voices":
      return (o.added ?? 0) > 1 ? `${o.added} notes s'ajoutent à l'accord` : "une note s'ajoute à l'accord";

    // Les deux mains ensemble : ce sont des instants frappés, pas une main.
    case "texture":
      return `on frappe ${o.after ?? 0} fois au lieu de ${o.before ?? 0} sur cet accord`;

    case "melody":
      return "la mélodie arrive";

    case "ornament": {
      const named = Object.entries(o.figures ?? {}).map(([kind, n]) => {
        const [one, many] = ORNAMENTS[kind] ?? [kind, kind];

        return n > 1 ? `${n} ${many}` : one;
      });

      return named.length > 0 ? `hors de l'accord : ${list(named)}` : "des notes hors accord arrivent";
    }

    case "unnamed":
      return "des notes que l'analyse ne nomme pas arrivent, et elles se jouent quand même";
  }
}

/**
 * Ce que la basse fait après sa première note, quand elle fait quelque chose.
 *
 * Une incise, jamais une seconde phrase : ce qui précède est le fait du niveau,
 * et ce qui suit n'est vrai que de cette fenêtre-là.
 *
 * | mouvement | part | ce qu'on en dit |
 * |---|---|---|
 * | `once`    |  8,8 % | rien : le nom de la note est déjà tout |
 * | `repeat`  |  2,5 % | un mot, et pas une leçon : rien de neuf n'a sonné |
 * | `octave`  |  4,5 % | un mot, et pas une leçon : c'est de la texture |
 * | `walks`   | 61,7 % | les degrés atteints, qui sont le sujet du niveau |
 * | `passing` | 22,6 % | qu'elle sort de l'accord, sans nommer la note |
 *
 * L'octave mérite qu'on insiste sur ce qu'on ne fait pas. La même note reprise
 * douze demi-tons plus bas ne change aucune harmonie, et lui donner une phrase
 * d'explication apprendrait au joueur qu'elle en est une. Elle se nomme donc au
 * passage, comme on nomme une couleur, et l'écran passe à autre chose.
 *
 * `passing` est l'inverse : c'est le seul de ces mouvements qui soit un
 * concept. La basse quitte l'harmonie et n'y appartient plus le temps d'une
 * note, donc elle **relie** deux accords au lieu de les énoncer, et c'est le
 * début de la conduite de la basse. Une fenêtre sur quatre. La phrase reprend
 * les mots que la nature `ornament` emploie pour la même chose — « hors de
 * l'accord », « passe » — parce que c'est la même chose, vue d'une autre main.
 *
 * Elle ne nomme aucune note, et ce n'est pas une prudence de rédaction : une
 * note étrangère n'a pas forcément de degré dans l'accord, donc il n'y a rien
 * à nommer qui soit vrai à tous les coups.
 *
 * Le champ est facultatif dans la charge utile : sans lui, la phrase est
 * exactement celle d'avant, mot pour mot.
 */
function bassMotion(o: Observation, entersInChord: boolean): string {
  // « Elle » est toujours la main gauche : chaque tête la pose en sujet.
  switch (o.motion) {
    case "passing":
      // Sans « puis » : la note hors de l'accord peut être la première, suivie
      // d'un retour dans l'harmonie. Et quand la tête vient de le dire, la
      // ligne ne fait que bouger.
      return entersInChord ? ", et sa ligne sort de l'accord" : ", puis elle bouge";

    case "walks": {
      // Les degrés atteints après la première note, dédoublonnés et **privés de
      // celui d'où la basse part**. Sans ça, la mesure 14 de *Libérée,
      // délivrée* (si♭1, si♭2, fa2) annonçait « puis elle passe à la
      // fondamentale et à la quinte » : reprendre sa propre note une octave
      // plus haut n'est pas passer à un degré, c'est l'octave, et le mouvement
      // porte déjà ce fait. Ce qu'on nomme, c'est ce que la basse va chercher
      // ailleurs.
      const from = o.degrees?.[0];
      const reached = [
        ...new Set(
          (o.degrees ?? [])
            .slice(1)
            .filter((d) => from === undefined || simpleDegree(d.number) !== simpleDegree(from.number))
            .map(ordinal),
        ),
      ];

      return reached.length === 0
        ? ", puis elle bouge dans l'accord"
        : `, puis elle passe ${list(reached.map((name) => `à la ${name}`))}`;
    }

    // Un verbe et non un participe : après « l'accord se pose dessus »,
    // « reprise à l'octave » ne s'accordait plus à rien.
    case "octave":
      return ", puis elle la reprend à l'octave";

    case "repeat":
      return ", puis elle la refrappe telle quelle";

    default:
      return "";
  }
}

const ORDINALS: Record<number, string> = {
  1: "fondamentale",
  2: "seconde",
  3: "tierce",
  4: "quarte",
  5: "quinte",
  6: "sixte",
  7: "septième",
  8: "octave",
  9: "neuvième",
  10: "dixième",
  11: "onzième",
  12: "douzième",
};

/**
 * Le degré ramené dans l'octave, de 1 à 7.
 *
 * `degreesOf` compte à partir de la fondamentale posée **au ras de la plus
 * grave** de la fenêtre, donc une note deux octaves plus haut revient en
 * quinzième et non en fondamentale. Sur les 3 106 observations de basse du
 * catalogue, **19,2 %** des premières notes sortent au-delà du septième degré,
 * et l'écran annonçait « c'est la 19e de l'accord ».
 *
 * Nommer un son de l'accord est une question sans octave : une dixième est une
 * tierce, une quinzième est la fondamentale. On replie donc au moment de
 * nommer, et pas plus tôt : ce que l'analyseur publie reste le fait complet,
 * registre compris.
 */
function simpleDegree(number: number): number {
  return ((number - 1) % 7) + 1;
}

function ordinal(degree: FigureDegree): string {
  const alteration = degree.alteration ?? 0;
  const name = ORDINALS[simpleDegree(degree.number)] ?? `${degree.number}e`;

  if (alteration === 0) {
    return name;
  }

  return `${name} ${alteration < 0 ? "abaissée" : "augmentée"}`;
}
